import React, { useState, useEffect, useRef, lazy } from "react";
import { ColumnTypes } from "components/collections/util/datatypes";
import { getDate, getObjectComponent } from "components/collections/util/format";
import l from "components/util/l";

const CellFile = lazy(() => import("components/collections/table/Cell.lib.file"));
const CellString = lazy(() => import("components/collections/table/Cell.lib.string"));
const CellFloat = lazy(() => import("components/collections/table/Cell.lib.float"));
const CellDate = lazy(() => import("components/collections/table/Cell.lib.date"));
const CellBoolean = lazy(() => import("components/collections/table/Cell.lib.boolean"));
const CellCollection = lazy(() => import("components/collections/table/Cell.lib.collection"));
const CellUser = lazy(() => import("components/collections/table/Cell.lib.user"));
const CellLocation = lazy(() => import("components/collections/table/Cell.lib.location"));
const CellEnum = lazy(() => import("components/collections/table/Cell.lib.enum"));
const CellTime = lazy(() => import("components/collections/table/Cell.lib.time"));
const CellDatetime = lazy(() => import("components/collections/table/Cell.lib.datetime"));

const Cell = ({
  flow,
  row,
  column,
  value,
  collectionOptions = {},
  isHighlighted,
  isReadOnly,
  isActive,
  isEditingCell,
  showAdd,
  showDelete,
  showFilters,
  setSelectedFile,
  setIsEditingCell,
  setIsFilterModalShown,
  setFilterDefault,
  onClickCell = () => { },
  onDoubleClickCell = () => { },
  onBlurCell = () => { },
  onChangeRow = () => { },
  onClickInsertRow = () => { },
  onClickCloneRow = () => { },
  onClickDeleteRow = () => { },
  onTypeEnter = () => { },
}) => {

  isReadOnly = isReadOnly || column?.read_only;

  const cellRef = useRef(null);
  const contextMenuRef = useRef(null);
  const inputRef = useRef(null);

  const [isContextMenuShown, setIsContextMenuShown] = useState(false);

  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const componentId = useRef(Math.random().toString(36).substring(7));

  useEffect(() => {
    if (!isEditingCell && isActive || !isActive || isReadOnly || column.type == ColumnTypes.OBJECT) return;
    inputRef?.current?.focus();
  }, [isActive, isEditingCell && isActive, column, isReadOnly]);

  const onClickCopy = () => {

    let copyValue = value;
    if (column.type == ColumnTypes.FILE) copyValue = value?.url;
    else if (column.type == ColumnTypes.OBJECT) copyValue = JSON.stringify(value);
    else if (column.type == ColumnTypes.DATE) copyValue = getDate(value, "DD/MM/YYYY");
    else if (column.type == ColumnTypes.BOOLEAN) copyValue = value ? "Si" : "No";
    else if (column.type == ColumnTypes.COLLECTION) copyValue = value?.pk_label;
    else value = value?.toString();

    navigator.clipboard.writeText(copyValue);
    setIsContextMenuShown(false);

  }

  const onClickFilter = () => {
    setIsFilterModalShown(true);
    setIsContextMenuShown(false);
    setFilterDefault(column.name);
  }

  const onContextMenu = (e) => {

    e.preventDefault();
    setIsContextMenuShown(true);

    const x = e.clientX - cellRef.current.getBoundingClientRect().x;
    const y = e.clientY - cellRef.current.getBoundingClientRect().y;
    setCoords({ x, y });

  }

  if (column.visible === false) return null;

  const cellStyles = {};
  isReadOnly = isReadOnly || column.internal;

  const commonProps = {
    componentId,
    cellRef,
    inputRef,
    column,
    value,
    isActive,
    isEditingCell,
    isReadOnly,
    onChangeRow,
    setIsEditingCell,
  }

  let bg = "";
  if (row?.action == "delete") bg = "table-danger";
  else if (row?.action == "create") bg = "table-success";
  else if (isHighlighted) bg = "table-warning";
  // else if (row?.action == "update") bg = "";

  const handleCallback = (callbackName, e) => {

    if (cellRef?.current?.getAttribute("ignore-events") == "true") 
      return;

    if (callbackName == "onClickCell") onClickCell(e);
    else if (callbackName == "onDoubleClickCell") onDoubleClickCell(e);
    else if (callbackName == "onBlurCell") onBlurCell(e);
    else if (callbackName == "onContextMenu") onContextMenu(e);

  }

  return (
    <td
      ref={cellRef}
      className={"m-0 py-2 border table-record " + (isActive ? "active" : "") + " " + bg}
      onClick={(e) => handleCallback("onClickCell", e)}
      onDoubleClick={(e) => handleCallback("onDoubleClickCell", e)}
      onBlur={(e) => handleCallback("onBlurCell", e)}
      onContextMenu={(e) => handleCallback("onContextMenu", e)}
      style={{
        ...cellStyles,
        position: "relative",
        cursor: isActive ? "text" : "default",
      }}
    >

      <div className="d-flex align-items-center w-100 h-100">
        <div className="text-truncate d-flex flex-wrap align-items-center h-100 w-100">

          {
            column.type === ColumnTypes.FILE &&
            <CellFile
              {...commonProps}
              flowId={flow.id}
              setSelectedFile={setSelectedFile}
            />
          }

          {
            column.type === ColumnTypes.STRING &&
            <CellString
              {...commonProps}
              onTypeEnter={onTypeEnter}
            />
          }

          {
            column.type === ColumnTypes.FLOAT &&
            <CellFloat
              {...commonProps}
              onTypeEnter={onTypeEnter}
            />
          }

          {
            column.type === ColumnTypes.DATE &&
            <CellDate
              {...commonProps}
            />
          }

          {
            column.type === ColumnTypes.BOOLEAN &&
            <CellBoolean
              {...commonProps}
            />
          }

          {
            column.type === ColumnTypes.COLLECTION &&
            <CellCollection
              {...commonProps}
              collectionOptions={collectionOptions}
              setSelectedFile={setSelectedFile}
              setIsContextMenuShown={setIsContextMenuShown}
            />
          }

          {
            column.type === ColumnTypes.USER &&
            <CellUser
              {...commonProps}
              flow={flow}
            />
          }

          {
            column.type === ColumnTypes.LOCATION &&
            <CellLocation
              {...commonProps}
            />
          }

          {
            column.type === ColumnTypes.ENUM &&
            <CellEnum
              {...commonProps}
            />
          }

          {
            column.type == ColumnTypes.OBJECT && <>
              {getObjectComponent(value)}
            </>
          }

          {
            column.type == ColumnTypes.TIME &&
            <CellTime
              {...commonProps}
            />
          }

          {
            column.type == ColumnTypes.DATE_TIME &&
            <CellDatetime
              {...commonProps}
            />
          }

        </div>
      </div>

      {
        isContextMenuShown && (
          <div
            ref={contextMenuRef}
            class="dropdown show option-menu"
            style={{
              position: "absolute",
              top: coords.y,
              left: coords.x,
              zIndex: 1000,
            }}
          >
            <div class="dropdown-menu show" aria-labelledby="add-column-dropdown">

              {
                !isReadOnly && showAdd &&
                <div>
                  <button
                    className="dropdown-item"
                    type="button"
                    ignore-onclickoutside={componentId.current}
                    onClick={() => {
                      setIsContextMenuShown(false);
                      onClickInsertRow();
                    }}
                  >
                    <i class="fa-solid fa-arrow-up dropdown-item-icon"></i> {l("Insertar fila arriba%CA059")}
                  </button>
                </div>
              }

              {
                !isReadOnly && showAdd &&
                <div>
                  <button
                    className="dropdown-item"
                    type="button"
                    ignore-onclickoutside={componentId.current}
                    onClick={() => {
                      setIsContextMenuShown(false);
                      onClickCloneRow();
                    }}
                  >
                    <i class="fa-solid fa-clone dropdown-item-icon"></i> {l("Clonar registro%CA159")}
                  </button>
                </div>
              }

              <button
                className="dropdown-item"
                type="button"
                ignore-onclickoutside={componentId.current}
                onClick={onClickCopy}
              >
                <i class="fa-solid fa-copy dropdown-item-icon"></i> {l("Copiar valor%CA060")}
              </button>

              {
                showFilters &&
                <button
                  type="button"
                  className="dropdown-item"
                  ignore-onclickoutside={componentId.current}
                  onClick={onClickFilter}
                >
                  <i class="fa-solid fa-filter dropdown-item-icon"></i> {l("Filtrar%CA061")}
                </button>
              }

              {
                !isReadOnly && showDelete &&
                <div>
                  <hr className="my-2" />
                  <button
                    className="dropdown-item"
                    type="button"
                    ignore-onclickoutside={componentId.current}
                    onClick={() => {
                      setIsContextMenuShown(false);
                      onClickDeleteRow();
                    }}
                  >
                    <i class="fa-solid fa-trash dropdown-item-icon"></i> {l("Eliminar fila%CA062")}
                  </button>

                </div>
              }

            </div>
          </div>
        )
      }

    </td>
  );
}

export default Cell;